// ══════════════════════════════════════════════════════════
// SUBSCRIPTION TAB — Admin Settings
//
// Muestra la suscripción real de la organización desde Supabase.
// NO modifica plan_id, status, price, renewal date ni activa
// suscripciones desde el frontend.
// ══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getSupabaseClient } from '@/services/supabase/client';
import { SwalAlert } from '@/hooks/useSweetAlert';

// ── TYPES ──

type SubscriptionStatus =
  | 'active' | 'trialing' | 'pending' | 'past_due'
  | 'cancel_at_period_end' | 'cancelled' | 'expired' | 'failed';

interface SubscriptionFullData {
  subscription_id: string;
  status: SubscriptionStatus;
  plan_id: string;
  plan_name: string;
  plan_slug: string;
  plan_description: string;
  price_monthly: number;
  price_yearly: number | null;
  currency: string;
  billing_period: string | null;
  starts_at: string | null;
  ends_at: string | null;
  canceled_at: string | null;
  auto_renew: boolean;
  feature_codes: string[];
  plan_features: Record<string, string | number | boolean>;
  storage_gb: number;
  max_announcers: number;
  max_stations: number;
  has_managed_streaming: boolean;
  has_autodj: boolean;
  has_custom_domain: boolean;
  has_custom_app: boolean;
  has_lives: boolean;
  pending_checkout_id: string | null;
  pending_checkout_url: string | null;
}

interface FeatureDisplay {
  code: string;
  label: string;
  icon: string;
  enabled: boolean;
  category: 'streaming' | 'content' | 'community' | 'professional' | 'limits';
}

const FEATURE_DEFINITIONS: Omit<FeatureDisplay, 'enabled'>[] = [
  { code: 'managed_streaming', label: 'Streaming Administrado', icon: 'ri-cloud-line', category: 'streaming' },
  { code: 'autodj', label: 'AutoDJ', icon: 'ri-disc-line', category: 'streaming' },
  { code: 'extra_storage', label: 'Almacenamiento Extra', icon: 'ri-hard-drive-2-line', category: 'content' },
  { code: 'multiple_stations', label: 'Estaciones Múltiples', icon: 'ri-stack-line', category: 'streaming' },
  { code: 'advanced_cabina', label: 'Cabina Avanzada', icon: 'ri-dashboard-3-line', category: 'community' },
  { code: 'podcasts', label: 'Podcasts', icon: 'ri-headphone-line', category: 'content' },
  { code: 'contests', label: 'Concursos', icon: 'ri-trophy-line', category: 'community' },
  { code: 'analytics', label: 'Analítica Avanzada', icon: 'ri-bar-chart-2-line', category: 'professional' },
  { code: 'custom_domain', label: 'Dominio Personalizado', icon: 'ri-global-line', category: 'professional' },
  { code: 'featured_radio', label: 'Radio Destacada', icon: 'ri-star-line', category: 'professional' },
  { code: 'advanced_notifications', label: 'Notificaciones Avanzadas', icon: 'ri-notification-3-line', category: 'community' },
  { code: 'custom_app', label: 'App Personalizada', icon: 'ri-smartphone-line', category: 'professional' },
];

// ── STATUS META ──

interface StatusMeta {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
  description: string;
}

const STATUS_META: Record<SubscriptionStatus, StatusMeta> = {
  active: {
    label: 'Activa',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: 'ri-checkbox-circle-fill text-emerald-500',
    description: 'Tu suscripción está activa. Todas las funciones premium están disponibles.',
  },
  trialing: {
    label: 'Período de Prueba',
    color: 'text-sky-700',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    icon: 'ri-time-fill text-sky-500',
    description: 'Estás en período de prueba. Disfrutá todas las funciones premium sin costo.',
  },
  pending: {
    label: 'Pendiente',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'ri-hourglass-fill text-amber-500',
    description: 'El pago está siendo procesado. Las funciones premium se activarán al confirmarse.',
  },
  past_due: {
    label: 'Pago Vencido',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'ri-error-warning-fill text-red-500',
    description: 'Tenés un pago pendiente. Actualizá tu método de pago para mantener las funciones activas.',
  },
  cancel_at_period_end: {
    label: 'Cancelación Programada',
    color: 'text-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    icon: 'ri-calendar-close-fill text-orange-500',
    description: 'La cancelación está programada. Seguí disfrutando las funciones hasta el final del período.',
  },
  cancelled: {
    label: 'Cancelada',
    color: 'text-foreground-600',
    bg: 'bg-foreground-100',
    border: 'border-foreground-200',
    icon: 'ri-close-circle-fill text-foreground-400',
    description: 'Tu suscripción fue cancelada. Las funciones premium ya no están disponibles.',
  },
  expired: {
    label: 'Expirada',
    color: 'text-foreground-600',
    bg: 'bg-foreground-100',
    border: 'border-foreground-200',
    icon: 'ri-timer-fill text-foreground-400',
    description: 'Tu suscripción expiró. Renová para recuperar las funciones premium.',
  },
  failed: {
    label: 'Fallida',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'ri-close-circle-fill text-red-500',
    description: 'El pago no pudo procesarse. Intentá nuevamente o contactá a soporte.',
  },
};

// ── HELPERS ──

function supabase() {
  return getSupabaseClient();
}

function formatPrice(amount: number, currency: string): string {
  if (amount === 0) return 'Gratis';
  const symbol = currency === 'USD' ? '$' : currency;
  return `${symbol}${amount}/mes`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatFullDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
}

function parseFeatures(raw: unknown): Record<string, string | number | boolean> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const record = raw as Record<string, unknown>;
  const safe: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      safe[key] = value;
    }
  }
  return safe;
}

function resolveNumber(features: Record<string, string | number | boolean>, key: string, fallback: number): number {
  const v = features[key];
  if (typeof v === 'number') return v;
  if (typeof v === 'string') { const n = parseFloat(v); if (!Number.isNaN(n)) return n; }
  return fallback;
}

function isActiveStatus(status: SubscriptionStatus): boolean {
  return status === 'active' || status === 'trialing' || status === 'cancel_at_period_end';
}

function isTerminalStatus(status: SubscriptionStatus): boolean {
  return status === 'cancelled' || status === 'expired' || status === 'failed';
}

// ═══════════════ FETCH FULL SUBSCRIPTION ═══════════════

async function fetchFullSubscription(): Promise<SubscriptionFullData | null> {
  // 1. Profile → organization_id
  const { data: profile, error: profileError } = await supabase()
    .from('profiles')
    .select('organization_id')
    .single();

  if (profileError || !profile?.organization_id) return null;
  const orgId = profile.organization_id as string;

  // 2. Latest subscription (any status)
  const { data: sub, error: subError } = await supabase()
    .from('subscriptions')
    .select('*')
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (subError || !sub) return null;

  // 3. Plan
  const { data: plan, error: planError } = await supabase()
    .from('plans')
    .select('*')
    .eq('id', sub.plan_id as string)
    .maybeSingle();

  if (planError || !plan) return null;

  // 4. Feature codes
  const { data: features, error: featsError } = await supabase()
    .from('plan_features')
    .select('feature_code')
    .eq('plan_id', plan.id as string);

  const featureCodes = featsError ? [] : (features || []).map((f) => f.feature_code as string);
  const parsedFeatures = parseFeatures(plan.features);

  // 5. Pending checkout
  let pendingCheckoutId: string | null = null;
  let pendingCheckoutUrl: string | null = null;

  if (sub.status === 'pending') {
    const { data: checkout } = await supabase()
      .from('checkouts')
      .select('id, checkout_url')
      .eq('organization_id', orgId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (checkout) {
      pendingCheckoutId = checkout.id as string;
      pendingCheckoutUrl = checkout.checkout_url as string | null;
    }
  }

  return {
    subscription_id: sub.id as string,
    status: sub.status as SubscriptionStatus,
    plan_id: plan.id as string,
    plan_name: plan.name as string,
    plan_slug: plan.slug as string,
    plan_description: (plan.description as string) || '',
    price_monthly: Number(plan.price_monthly ?? 0),
    price_yearly: plan.price_yearly ? Number(plan.price_yearly) : null,
    currency: (plan.currency as string) || 'USD',
    billing_period: (sub.billing_period as string) || null,
    starts_at: sub.starts_at as string | null,
    ends_at: sub.ends_at as string | null,
    canceled_at: sub.canceled_at as string | null,
    auto_renew: (sub.auto_renew as boolean) ?? false,
    feature_codes: featureCodes,
    plan_features: parsedFeatures,
    storage_gb: resolveNumber(parsedFeatures, 'storage_gb', 1),
    max_announcers: resolveNumber(parsedFeatures, 'max_announcers', 1),
    max_stations: resolveNumber(parsedFeatures, 'max_stations', 1),
    has_managed_streaming: featureCodes.includes('managed_streaming'),
    has_autodj: featureCodes.includes('autodj'),
    has_custom_domain: featureCodes.includes('custom_domain'),
    has_custom_app: featureCodes.includes('custom_app'),
    has_lives: resolveNumber(parsedFeatures, 'max_lives', 0) > 0 || resolveNumber(parsedFeatures, 'max_lives', 0) === -1,
    pending_checkout_id: pendingCheckoutId,
    pending_checkout_url: pendingCheckoutUrl,
  };
}

// ═══════════════ COMPONENT ═══════════════

export default function SubscriptionTab() {
  const [data, setData] = useState<SubscriptionFullData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sub = await fetchFullSubscription();
      setData(sub);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar la suscripción.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Cancel subscription ──
  const handleCancelRequest = useCallback(async () => {
    if (!data?.subscription_id) return;

    const result = await Swal.fire({
      title: '¿Cancelar suscripción?',
      html: `<p style="font-size:13px;margin-bottom:8px">Vas a solicitar la cancelación de tu plan <strong>${data.plan_name}</strong>.</p><p style="font-size:12px;color:#6b7280">La cancelación se procesará según los términos de tu plan. Mientras tanto, seguís teniendo acceso a todas las funciones.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, mantener',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#9ca3af',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        htmlContainer: 'text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
      },
      buttonsStyling: false,
    });

    if (!result.isConfirmed) return;

    setCancelling(true);
    try {
      // Registrar solicitud en metadata — el backend se encarga del resto
      const { error: updateError } = await supabase()
        .from('subscriptions')
        .update({
          metadata: {
            cancel_requested_at: new Date().toISOString(),
            cancel_requested_by: 'admin_settings',
          },
        })
        .eq('id', data.subscription_id);

      if (updateError) throw new Error(updateError.message);

      await SwalAlert.success(
        'Cancelación solicitada',
        'Tu solicitud de cancelación fue registrada. El equipo la procesará y te notificará.',
      );

      // Refresh
      await loadData();
    } catch (err) {
      SwalAlert.error(
        'Error',
        err instanceof Error ? err.message : 'No se pudo procesar la solicitud.',
      );
    } finally {
      setCancelling(false);
    }
  }, [data, loadData]);

  // ── Render ──

  if (loading) {
    return <SubscriptionSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-10 text-center">
        <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <i className="ri-error-warning-line text-red-500 text-2xl"></i>
        </div>
        <h3 className="text-foreground-950 font-bold text-sm mb-1">Error al cargar</h3>
        <p className="text-foreground-400 text-xs mb-4">{error}</p>
        <button
          onClick={loadData}
          className="px-5 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-1.5"></i>Reintentar
        </button>
      </div>
    );
  }

  // ── NO SUBSCRIPTION ──
  if (!data) {
    return <EmptyState />;
  }

  // ── HAS SUBSCRIPTION ──
  const meta = STATUS_META[data.status] || STATUS_META.cancelled;

  return (
    <div className="space-y-6">
      {/* ── STATUS BANNER ── */}
      <div className={`${meta.bg} ${meta.border} border rounded-2xl p-5`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-xl ${meta.bg} border ${meta.border} flex items-center justify-center shrink-0`}>
              <i className={`${meta.icon} text-xl`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-foreground-950 font-bold text-base">{data.plan_name}</h3>
                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${meta.color} ${meta.bg} border ${meta.border}`}>
                  {meta.label}
                </span>
              </div>
              <p className="text-foreground-500 text-[11px]">{meta.description}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {isActiveStatus(data.status) && (
              <>
                <Link
                  to="/pricing"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-[11px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-up-circle-line"></i>
                  Cambiar Plan
                </Link>
                {data.status !== 'cancel_at_period_end' && (
                  <button
                    onClick={handleCancelRequest}
                    disabled={cancelling}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 text-foreground-500 text-[11px] font-semibold hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {cancelling ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Cancelando...
                      </>
                    ) : (
                      <>
                        <i className="ri-close-circle-line"></i>
                        Cancelar
                      </>
                    )}
                  </button>
                )}
              </>
            )}

            {data.status === 'pending' && data.pending_checkout_url && (
              <a
                href={data.pending_checkout_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary-500 text-background-50 text-[11px] font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Completar Pago
              </a>
            )}

            {isTerminalStatus(data.status) && (
              <Link
                to="/pricing"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-[11px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-circle-line"></i>
                Elegir Plan
              </Link>
            )}

            {data.status === 'past_due' && (
              <Link
                to="/pricing"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-[11px] font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                Actualizar Pago
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* ── PLAN DETAILS GRID ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <DetailCard
          icon="ri-money-dollar-circle-line"
          label="Precio"
          value={formatPrice(data.price_monthly, data.currency)}
          sub={data.price_yearly ? `${data.currency === 'USD' ? '$' : data.currency}${data.price_yearly}/año` : undefined}
        />
        <DetailCard
          icon="ri-calendar-check-line"
          label="Inicio"
          value={formatDate(data.starts_at)}
        />
        <DetailCard
          icon="ri-calendar-event-line"
          label={data.auto_renew ? 'Próxima Renovación' : 'Vencimiento'}
          value={formatDate(data.ends_at)}
        />
        <DetailCard
          icon="ri-refresh-line"
          label="Renovación"
          value={data.auto_renew ? 'Automática' : 'Manual'}
          valueClass={data.auto_renew ? 'text-emerald-600' : 'text-amber-600'}
        />
      </div>

      {/* Cancelation info */}
      {(data.canceled_at || data.status === 'cancel_at_period_end') && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-start gap-2.5">
          <i className="ri-calendar-close-line text-orange-500 text-base shrink-0 mt-0.5"></i>
          <div>
            <p className="text-orange-800 text-[11px] font-semibold mb-0.5">
              {data.status === 'cancel_at_period_end' ? 'Cancelación programada' : 'Cancelada'}
            </p>
            <p className="text-orange-600 text-[10px]">
              {data.canceled_at
                ? `Solicitada el ${formatFullDate(data.canceled_at)}`
                : 'La suscripción se cancelará al final del período actual.'}
              {' '}Seguís teniendo acceso a todas las funciones hasta entonces.
            </p>
          </div>
        </div>
      )}

      {/* ── FEATURES ── */}
      <div>
        <h4 className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-3">
          Funciones del Plan
        </h4>
        <FeaturesGrid featureCodes={data.feature_codes} />
      </div>

      {/* ── LIMITS ── */}
      <div>
        <h4 className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-3">
          Límites y Capacidad
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <LimitCard
            icon="ri-hard-drive-2-line"
            label="Almacenamiento"
            value={data.storage_gb === -1 || data.storage_gb >= 999 ? 'Ilimitado' : `${data.storage_gb} GB`}
          />
          <LimitCard
            icon="ri-stack-line"
            label="Estaciones"
            value={data.max_stations === -1 ? 'Ilimitadas' : String(data.max_stations)}
          />
          <LimitCard
            icon="ri-mic-line"
            label="Locutores"
            value={data.max_announcers === -1 ? 'Ilimitados' : String(data.max_announcers)}
          />
          <LimitCard
            icon="ri-broadcast-line"
            label="Streaming"
            value={data.has_managed_streaming ? 'RFW Administrado' : 'Externo'}
          />
          <LimitCard
            icon="ri-live-line"
            label="Lives"
            value={data.has_lives ? 'Ilimitadas' : '0'}
          />
          <LimitCard
            icon="ri-disc-line"
            label="AutoDJ"
            value={data.has_autodj ? 'Incluido' : 'No incluido'}
            valueClass={data.has_autodj ? 'text-emerald-600' : 'text-foreground-400'}
          />
          <LimitCard
            icon="ri-global-line"
            label="Dominio"
            value={data.has_custom_domain ? 'Personalizado' : 'Subdominio'}
            valueClass={data.has_custom_domain ? 'text-emerald-600' : 'text-foreground-400'}
          />
          <LimitCard
            icon="ri-smartphone-line"
            label="App Móvil"
            value={data.has_custom_app ? 'Incluida' : 'No incluida'}
            valueClass={data.has_custom_app ? 'text-emerald-600' : 'text-foreground-400'}
          />
        </div>
      </div>
    </div>
  );
}

// ═══════════════ SUB-COMPONENTS ═══════════════

function SubscriptionSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-24 bg-background-100 rounded-2xl"></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-20 bg-background-100 rounded-2xl"></div>
        ))}
      </div>
      <div className="h-40 bg-background-100 rounded-2xl"></div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="space-y-6">
      {/* Free plan banner */}
      <div className="bg-secondary-50 border border-secondary-200 rounded-2xl p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-secondary-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-vip-line text-secondary-500 text-2xl"></i>
        </div>
        <h3 className="text-foreground-950 font-bold text-sm mb-2">Plan Free</h3>
        <p className="text-foreground-500 text-xs mb-4 max-w-md mx-auto">
          Estás usando el plan gratuito. Contás con perfil profesional, stream en vivo y cabina básica sin costo. Mejorá tu plan para acceder a funciones premium.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-arrow-up-circle-line"></i>
          Ver Planes
        </Link>
      </div>

      {/* Free plan features */}
      <div>
        <h4 className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-3">
          Funciones del Plan Free
        </h4>
        <FeaturesGrid featureCodes={[]} />
      </div>

      {/* Free plan limits */}
      <div>
        <h4 className="text-[11px] font-semibold text-foreground-400 uppercase tracking-wider mb-3">
          Límites y Capacidad
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <LimitCard icon="ri-hard-drive-2-line" label="Almacenamiento" value="1 GB" />
          <LimitCard icon="ri-stack-line" label="Estaciones" value="1" />
          <LimitCard icon="ri-mic-line" label="Locutores" value="1" />
          <LimitCard icon="ri-broadcast-line" label="Streaming" value="Externo" />
          <LimitCard icon="ri-live-line" label="Lives" value="0" />
          <LimitCard icon="ri-disc-line" label="AutoDJ" value="No incluido" valueClass="text-foreground-400" />
          <LimitCard icon="ri-global-line" label="Dominio" value="Subdominio" valueClass="text-foreground-400" />
          <LimitCard icon="ri-smartphone-line" label="App Móvil" value="No incluida" valueClass="text-foreground-400" />
        </div>
      </div>
    </div>
  );
}

function DetailCard({ icon, label, value, sub, valueClass }: {
  icon: string;
  label: string;
  value: string;
  sub?: string;
  valueClass?: string;
}) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
          <i className={`${icon} text-foreground-500 text-sm`}></i>
        </div>
        <span className="text-[10px] text-foreground-400 font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-foreground-950 font-bold text-sm ${valueClass || ''}`}>{value}</p>
      {sub && <p className="text-foreground-400 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}

function LimitCard({ icon, label, value, valueClass }: {
  icon: string;
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
          <i className={`${icon} text-foreground-500 text-sm`}></i>
        </div>
        <span className="text-[10px] text-foreground-400 font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-foreground-950 font-bold text-sm ${valueClass || ''}`}>{value}</p>
    </div>
  );
}

function FeaturesGrid({ featureCodes }: { featureCodes: string[] }) {
  const features = FEATURE_DEFINITIONS.map((def) => ({
    ...def,
    enabled: featureCodes.includes(def.code),
  }));

  const categories = ['streaming', 'content', 'community', 'professional'] as const;
  const categoryLabels: Record<string, string> = {
    streaming: 'Streaming',
    content: 'Contenido',
    community: 'Comunidad',
    professional: 'Profesional',
  };

  return (
    <div className="space-y-4">
      {categories.map((cat) => {
        const catFeatures = features.filter((f) => f.category === cat);
        if (catFeatures.length === 0) return null;

        return (
          <div key={cat}>
            <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wider mb-2">
              {categoryLabels[cat]}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {catFeatures.map((feat) => (
                <div
                  key={feat.code}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
                    feat.enabled
                      ? 'bg-background-50 border-background-200'
                      : 'bg-background-100/60 border-background-200/50 opacity-60'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    feat.enabled ? 'bg-emerald-100' : 'bg-background-200'
                  }`}>
                    <i className={`${feat.icon} text-sm ${feat.enabled ? 'text-emerald-600' : 'text-foreground-400'}`}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-xs font-semibold ${feat.enabled ? 'text-foreground-800' : 'text-foreground-400'}`}>
                      {feat.label}
                    </p>
                  </div>
                  <div>
                    {feat.enabled ? (
                      <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                        <i className="ri-check-line text-emerald-600 text-[10px]"></i>
                      </span>
                    ) : (
                      <span className="w-5 h-5 rounded-full bg-foreground-100 flex items-center justify-center">
                        <i className="ri-close-line text-foreground-400 text-[10px]"></i>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}